import React from 'react';
import {
    Button,
    Checkbox,
    Chip,
    Divider,
    IconButton,
    InputBase,
    Menu,
    MenuItem,
    MenuList,
    Paper,
    Popover,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import {getReportStatusChipColor} from '../../../utils/reportStatusColor';

const OPERATOR_OPTIONS = Object.freeze([
    {value: '=', label: 'equals'},
    {value: '!=', label: 'not equals'},
]);

// Field key whose values render as colored, capitalized status chips (matching the table).
const STATUS_KEY = 'status';

const getRowSummary = (values = []) => {
    if (!values.length) {
        return 'Any';
    }
    if (values.length === 1) {
        return values[0];
    }
    return `${values[0]} +${values.length - 1}`;
};

const BasicSearchFiltersPanel = ({
                                     rows,
                                     propertyOptions,
                                     defaultKeys,
                                     lockedKeys,
                                     hasUnsavedChanges,
                                     onToggleMoreFilter,
                                     onOperatorChange,
                                     onToggleValue,
                                     onClearValues,
                                     onRemoveFilter,
                                     onApply,
                                     onReset,
                                     onFilterChange,
                                 }) => {
    const defaultKeysSet = React.useMemo(
        () => new Set(Array.isArray(defaultKeys) ? defaultKeys : []),
        [defaultKeys],
    );
    const lockedKeysSet = React.useMemo(
        () => new Set(Array.isArray(lockedKeys) ? lockedKeys : []),
        [lockedKeys],
    );
    const [moreFiltersAnchor, setMoreFiltersAnchor] = React.useState(null);
    const [activeFilterKey, setActiveFilterKey] = React.useState(null);
    const [activeFilterAnchor, setActiveFilterAnchor] = React.useState(null);
    const [valuesSearch, setValuesSearch] = React.useState('');

    const filtersByKey = React.useMemo(
        () => propertyOptions.reduce((acc, item) => {
            acc[item.key] = item;
            return acc;
        }, {}),
        [propertyOptions],
    );
    const enabledPropertyKeys = React.useMemo(() => new Set(rows.map((row) => row.propertyKey)), [rows]);

    const activeRow = rows.find((row) => row.propertyKey === activeFilterKey) || null;
    const activeProperty = activeRow ? filtersByKey[activeRow.propertyKey] : null;
    const activeOperator = activeRow?.operator || '=';
    const activeValues = activeRow?.values || [];
    const activeSelectedValuesSet = new Set(activeValues);
    // Stable order: sort selected-first only once when the popover opens (when
    // activeFilterKey changes), then freeze it. Without this the list re-sorts
    // on every toggle and items visibly jump around while the user is selecting.
    const stableOrderRef = React.useRef([]);
    React.useEffect(() => {
        if (!activeFilterKey || !activeProperty) {
            stableOrderRef.current = [];
            return;
        }
        stableOrderRef.current = [...activeProperty.values].sort((a, b) => {
            const aSelected = activeSelectedValuesSet.has(a);
            const bSelected = activeSelectedValuesSet.has(b);
            if (aSelected !== bSelected) return aSelected ? -1 : 1;
            return a.localeCompare(b);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilterKey, activeProperty]);

    const searchTerm = valuesSearch.trim().toLowerCase();
    const visibleValues = (stableOrderRef.current.length ? stableOrderRef.current : activeProperty?.values ?? [])
        .filter((value) => value.toLowerCase().includes(searchTerm));

    React.useEffect(() => {
        if (activeFilterKey && !rows.some((row) => row.propertyKey === activeFilterKey)) {
            setActiveFilterKey(null);
            setActiveFilterAnchor(null);
            setValuesSearch('');
        }
    }, [activeFilterKey, rows]);

    const handleFilterOpen = React.useCallback((event, propertyKey) => {
        setActiveFilterKey(propertyKey);
        setActiveFilterAnchor(event.currentTarget);
        setValuesSearch('');
    }, []);

    const handleFilterClose = React.useCallback(() => {
        setActiveFilterKey(null);
        setActiveFilterAnchor(null);
        setValuesSearch('');
    }, []);

    const notify = React.useCallback(() => onFilterChange?.(), [onFilterChange]);

    const handleToggleValue = React.useCallback((key, value) => {
        onToggleValue(key, value);
        notify();
    }, [onToggleValue, notify]);

    const handleOperatorChange = React.useCallback((key, op) => {
        onOperatorChange(key, op);
        notify();
    }, [onOperatorChange, notify]);

    const handleClearValues = React.useCallback((key) => {
        onClearValues(key);
        notify();
    }, [onClearValues, notify]);

    const handleRemoveFilter = React.useCallback((key) => {
        onRemoveFilter(key);
        notify();
    }, [onRemoveFilter, notify]);

    const handleToggleMoreFilter = React.useCallback((key) => {
        onToggleMoreFilter(key);
        notify();
    }, [onToggleMoreFilter, notify]);

    return (
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            {rows.map((row) => {
                    const property = filtersByKey[row.propertyKey];
                    const summary = getRowSummary(row.values);
                    const isDefault = defaultKeysSet.has(row.propertyKey);
                    const isLocked = lockedKeysSet.has(row.propertyKey);

                    return (
                        <Stack
                            key={row.propertyKey}
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                        >
                            <Tooltip
                                title={isLocked ? 'This filter is set by the selected view and cannot be changed' : ''}
                                placement="top"
                                arrow
                                disableHoverListener={!isLocked}
                                disableFocusListener={!isLocked}
                                disableTouchListener={!isLocked}
                            >
                                <Paper
                                    variant="outlined"
                                    onClick={isLocked ? undefined : (event) => handleFilterOpen(event, row.propertyKey)}
                                    sx={{
                                        minWidth: 0,
                                        px: 1.25,
                                        py: 0.75,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        cursor: isLocked ? 'default' : 'pointer',
                                        opacity: isLocked ? 0.7 : 1,
                                        ...(!isLocked && {
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                                bgcolor: 'action.hover',
                                            },
                                        }),
                                    }}
                                >
                                    <Typography variant="body2">{property?.label || row.propertyKey}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {row.operator}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={row.propertyKey === STATUS_KEY ? {textTransform: 'capitalize'} : undefined}
                                    >
                                        {summary}
                                    </Typography>
                                    {isLocked
                                        ? <LockOutlinedIcon fontSize="small" color="disabled" sx={{fontSize: 14}} />
                                        : <ExpandMoreIcon fontSize="small" color="action"/>
                                    }
                                </Paper>
                            </Tooltip>
                            {!isDefault && !isLocked && (
                                <IconButton
                                    size="small"
                                    aria-label={`remove ${property?.label || row.propertyKey} filter`}
                                    onClick={() => handleRemoveFilter(row.propertyKey)}
                                >
                                    <CloseIcon fontSize="small"/>
                                </IconButton>
                            )}
                        </Stack>
                    );
                })}

            <Button
                size="small"
                variant="text"
                onClick={(event) => { setMoreFiltersAnchor(event.currentTarget); }}
            >
                Add filter
            </Button>

            {onApply && (
                <Button size="small" variant="contained" onClick={onApply}>
                    Apply
                </Button>
            )}
            {hasUnsavedChanges && (
                <Typography variant="caption" color="warning.main" sx={{alignSelf: 'center'}}>
                    Unsaved changes
                </Typography>
            )}

            <Popover
                open={Boolean(activeFilterAnchor)}
                anchorEl={activeFilterAnchor}
                onClose={handleFilterClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                transformOrigin={{vertical: 'top', horizontal: 'left'}}
                slotProps={{
                    paper: {
                        sx: {
                            width: 320,
                            p: 0,
                        },
                    },
                }}
            >
                {activeRow && activeProperty && (
                    <Stack>
                        <Stack sx={{p: 1}}>
                            <TextField
                                select
                                size="small"
                                value={activeOperator}
                                onChange={(event) => handleOperatorChange(activeRow.propertyKey, event.target.value)}
                                fullWidth
                            >
                                {OPERATOR_OPTIONS.map((operatorOption) => (
                                    <MenuItem key={operatorOption.value} value={operatorOption.value}>
                                        {`${activeProperty.label} ${operatorOption.value} (${operatorOption.label})`}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>

                        <Stack sx={{px: 1, pb: 1}}>
                            <Paper
                                variant="outlined"
                                sx={{px: 1, display: 'flex', alignItems: 'center', gap: 0.75}}
                            >
                                <SearchIcon fontSize="small" color="action"/>
                                <InputBase
                                    value={valuesSearch}
                                    onChange={(event) => setValuesSearch(event.target.value)}
                                    placeholder={`Search ${activeProperty.label}`}
                                    fullWidth
                                />
                            </Paper>
                        </Stack>

                        <Divider/>

                        <MenuList dense disablePadding sx={{maxHeight: 260, overflowY: 'auto'}}>
                            {visibleValues.map((value) => (
                                <MenuItem
                                    key={value}
                                    dense
                                    disableRipple
                                    onClick={() => handleToggleValue(activeRow.propertyKey, value)}
                                >
                                    <Checkbox
                                        checked={activeSelectedValuesSet.has(value)}
                                        sx={{mr: 1}}
                                        size="small"
                                    />
                                    {activeRow.propertyKey === STATUS_KEY ? (
                                        <Chip
                                            label={value}
                                            size="small"
                                            color={getReportStatusChipColor(value)}
                                            variant="filled"
                                            sx={{textTransform: 'capitalize'}}
                                        />
                                    ) : (
                                        <Typography variant="body2">{value}</Typography>
                                    )}
                                </MenuItem>
                            ))}
                            {!visibleValues.length && (
                                <Typography variant="body2" color="text.secondary" sx={{px: 2, py: 1.5}}>
                                    No matches
                                </Typography>
                            )}
                        </MenuList>

                        <Divider/>

                        <Button
                            onClick={() => handleClearValues(activeRow.propertyKey)}
                            size="small"
                            sx={{justifyContent: 'flex-start', borderRadius: 0, px: 2, py: 1}}
                        >
                            Clear selection
                        </Button>
                    </Stack>
                )}
            </Popover>

            <Menu
                anchorEl={moreFiltersAnchor}
                open={Boolean(moreFiltersAnchor)}
                onClose={() => setMoreFiltersAnchor(null)}
            >
                {propertyOptions.map((property) => {
                    const checked = enabledPropertyKeys.has(property.key);
                    return (
                        <MenuItem
                            key={property.key}
                            onClick={() => handleToggleMoreFilter(property.key)}
                        >
                            <Checkbox checked={checked} size="small" sx={{mr: 1}}/>
                            {property.label}
                        </MenuItem>
                    );
                })}
            </Menu>
        </Stack>
    );
};

export default React.memo(BasicSearchFiltersPanel);
